/**
 * Skeleton — the single shared loading-placeholder primitive for the app.
 *
 * Subsumes the three previously-duplicated skeletons:
 *   • the workflows Skeleton (announced, role=status/aria-busy, `rounded`)
 *   • ChartSkeleton (decorative, rounded-lg, default height 400, full-width)
 *   • the MUI rectangular leaves inside DrawerBodySkeleton (sharp, decorative)
 *
 * Each of those keeps its existing path as a thin wrapper over this primitive so
 * no consumer import changes. The Outlook add-in keeps its own mirrored copy
 * (ui/CLAUDE.md rule #1 — mirror-by-copy, never cross-import).
 *
 * Variants:
 *   line  — text-row placeholder (plain gray, no firefly — used for StatCard values)
 *   block — generic block placeholder (plain gray)
 *   card  — card-shaped placeholder (plain gray)
 *   chart — large chart area; shows OraFireflyAnimated centered (always has height)
 *
 * Accessibility: announced by default (role=status, aria-busy, aria-label). The
 * `chart` variant is decorative by default, and any variant can be forced
 * decorative with `ariaHidden`.
 */
import type { CSSProperties } from "react";
// OraFireflyAnimated is SSR-safe: usePrefersReducedMotion uses useState(false)
// + useEffect so it never calls window.matchMedia during server render.
import { OraFireflyAnimated } from "~/common/components/OraFireflyAnimated";

export type SkeletonVariant = "line" | "block" | "card" | "chart";

export interface SkeletonProps {
  variant?: SkeletonVariant;
  /** CSS width — number → `${n}px`, string (incl. "100%") → literal. */
  width?: number | string;
  /** CSS height — number → `${n}px`, string → literal. `chart` defaults to 400. */
  height?: number | string;
  /** Appended AFTER base classes so caller classes win. */
  className?: string;
  /** Corner-radius override (e.g. "rounded-lg", or "" for sharp). Per-variant default otherwise. */
  rounded?: string;
  /** Repeat N times (e.g. list rows). Default 1. */
  count?: number;
  /** When set → role=status/aria-busy/aria-label="Loading" (announced to AT). */
  ariaLabel?: string;
  /** Force decorative (aria-hidden, no role) for any variant. */
  ariaHidden?: boolean;
}

function toCssDim(v: number | string | undefined): string | undefined {
  if (v === undefined) return undefined;
  return typeof v === "number" ? `${v}px` : v;
}

const BG = "bg-primary-gray-light-100";

// line/block/card: no hardcoded h-* or w-* so explicit width/height props win
// via inline style without Tailwind specificity fighting them.
const VARIANT: Record<
  SkeletonVariant,
  { base: string; defaultRounded: string; firefly: boolean }
> = {
  // line: text-row placeholder — too thin for any icon, no background.
  line:  { base: "",                                              defaultRounded: "rounded",    firefly: false },
  block: { base: "flex items-center justify-center",             defaultRounded: "rounded-md", firefly: true  },
  card:  { base: "flex items-center justify-center",             defaultRounded: "rounded-lg", firefly: true  },
  chart: { base: "flex items-center justify-center",             defaultRounded: "rounded-lg", firefly: true  },
};

export default function Skeleton({
  variant = "block",
  width,
  height,
  className,
  rounded,
  count = 1,
  ariaLabel,
  ariaHidden,
}: SkeletonProps) {
  const v = VARIANT[variant];
  const effHeight = height ?? (variant === "chart" ? 400 : undefined);

  const style: CSSProperties = {};
  const w = toCssDim(width);
  const h = toCssDim(effHeight);
  if (w) style.width = w;
  if (h) style.height = h;

  const radius = rounded ?? v.defaultRounded;
  const base = radius ? `${v.base} ${radius}` : v.base;
  const classes = className ? `${base} ${className}` : base;

  const decorative =
    ariaHidden === true ||
    (variant === "chart" && ariaHidden !== false && !ariaLabel);

  const a11y = decorative
    ? { "aria-hidden": true as const }
    : { role: "status", "aria-busy": true as const, "aria-label": ariaLabel ?? "Loading" };

  // Size firefly to the slot: chart gets 32px, block/card get 24px (StatCards
  // and other medium containers). line stays plain (no firefly).
  const fireflySize = variant === "chart" ? 32 : 24;
  const content = v.firefly ? (
    <OraFireflyAnimated size={fireflySize} tone="running" />
  ) : null;

  if (count > 1) {
    return (
      <>
        {Array.from({ length: count }).map((_, i) => (
          <div key={i} className={classes} style={style} {...a11y}>
            {content}
          </div>
        ))}
      </>
    );
  }
  return (
    <div className={classes} style={style} {...a11y}>
      {content}
    </div>
  );
}

export const LineSkeleton = (props: Omit<SkeletonProps, "variant">) => (
  <Skeleton variant="line" {...props} />
);
export const BlockSkeleton = (props: Omit<SkeletonProps, "variant">) => (
  <Skeleton variant="block" {...props} />
);
export const CardSkeleton = (props: Omit<SkeletonProps, "variant">) => (
  <Skeleton variant="card" {...props} />
);
